import { useState } from 'react';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Button from '@cloudscape-design/components/button';
import Table from '@cloudscape-design/components/table';
import Box from '@cloudscape-design/components/box';
import Badge from '@cloudscape-design/components/badge';
import Link from '@cloudscape-design/components/link';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Modal from '@cloudscape-design/components/modal';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import DatePicker from '@cloudscape-design/components/date-picker';
import { activitiesApi, CohortActivity } from '../../api/activities';
import { cohortsApi } from '../../api/cohorts';
import { mentorsApi } from '../../api/mentors';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryClient';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorAlert } from '../../components/ErrorAlert';
import { useNotifications } from '../../components/NotificationFlashbar';
import { useAuthStore } from '../../stores/authStore';

interface MyActivitiesTabProps {
  cohortId: string;
  userId: string;
  cohortStartDate?: string;
  mentorsEnabled?: boolean;
  graduationApproval?: 'automatic' | 'admin';
}

interface ActivityWithCompletion extends CohortActivity {
  completed?: boolean;
  pendingReview?: boolean;
  completedAt?: string;
  deliverableName?: string;
  deliverableLink?: string;
  recordingLink?: string;
  approvalStatus?: string;
}

/** Format a week's date range, e.g. "May 1 – May 7" */
function weekDateRange(weekNum: number, cohortStartDate?: string): string {
  if (!cohortStartDate) return '';
  // Append T00:00:00 to force local-time parsing and avoid UTC midnight off-by-one
  const start = new Date(cohortStartDate + 'T00:00:00');
  const weekStart = new Date(start);
  weekStart.setDate(start.getDate() + (weekNum - 1) * 7);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(weekStart)} – ${fmt(weekEnd)}`;
}

/** Group activities into week buckets */
function groupByWeek(activities: ActivityWithCompletion[]): {
  weeks: number[];
  byWeek: Record<number, ActivityWithCompletion[]>;
  unassigned: ActivityWithCompletion[];
} {
  const byWeek: Record<number, ActivityWithCompletion[]> = {};
  const unassigned: ActivityWithCompletion[] = [];

  for (const activity of activities) {
    if (activity.week != null) {
      if (!byWeek[activity.week]) byWeek[activity.week] = [];
      byWeek[activity.week].push(activity);
    } else {
      unassigned.push(activity);
    }
  }

  const sortByOrder = (a: ActivityWithCompletion, b: ActivityWithCompletion) => a.order - b.order;
  for (const week of Object.keys(byWeek)) byWeek[Number(week)].sort(sortByOrder);
  unassigned.sort(sortByOrder);

  const weeks = Object.keys(byWeek).map(Number).sort((a, b) => a - b);
  return { weeks, byWeek, unassigned };
}

export function MyActivitiesTab({ cohortId, userId, cohortStartDate, mentorsEnabled = false, graduationApproval = 'automatic' }: MyActivitiesTabProps) {
  const [selectedActivity, setSelectedActivity] = useState<ActivityWithCompletion | null>(null);
  const [completionDate, setCompletionDate] = useState('');
  const [deliverableName, setDeliverableName] = useState('');
  const [deliverableLink, setDeliverableLink] = useState('');
  const [recordingLink, setRecordingLink] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showGraduationModal, setShowGraduationModal] = useState(false);
  const [isRequestingGraduation, setIsRequestingGraduation] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const { showSuccess, showError } = useNotifications();
  const queryClient = useQueryClient();

  const {
    data: activities,
    isLoading: activitiesLoading,
    error: activitiesError,
  } = useQuery({
    queryKey: queryKeys.activities.list(cohortId),
    queryFn: () => activitiesApi.list(cohortId),
    enabled: !!cohortId,
  });

  const {
    data: completions,
  } = useQuery({
    queryKey: queryKeys.activities.completions(userId, cohortId),
    queryFn: () => activitiesApi.getUserCompletions(cohortId, userId),
    enabled: !!cohortId && !!userId,
  });

  // Fetch the user's assigned mentor — needed to block mentor-approval activities
  // when no mentor is assigned yet.
  const { data: mentorInfo } = useQuery({
    queryKey: queryKeys.mentors.mine(cohortId),
    queryFn: () => mentorsApi.getMentorForUser(cohortId),
    enabled: !!cohortId && !!userId && mentorsEnabled,
  });
  const hasMentor = !!mentorInfo?.mentorId;

  // Read graduation status from the auth store — already fetched by useUserPermissions on app load.
  // Graduated users are included in participantIn with status='graduated'.
  const participantIn = useAuthStore((state) => state.participantIn);
  const isAlreadyGraduated = participantIn.some(
    p => p.cohortId === cohortId && p.status === 'graduated'
  );

  const activitiesWithCompletion: ActivityWithCompletion[] = (activities || []).map(activity => {
    const completion = (completions || []).find((c) => c.activityId === activity.activityId);
    return {
      ...activity,
      completed: completion?.status === 'completed',
      pendingReview: completion?.status === 'pending_approval',
      completedAt: completion?.completedAt,
      deliverableName: completion?.deliverableName,
      deliverableLink: completion?.deliverableLink,
      recordingLink: completion?.recordingLink,
      approvalStatus: completion?.status,
    };
  });

  const completedCount = activitiesWithCompletion.filter(a => a.completed).length;
  const totalCount = activitiesWithCompletion.length;

  // Graduation eligibility: all required-for-graduation activities must be completed
  // (including mentor-approved ones — status must be 'completed', not just 'pending_approval')
  const requiredActivities = activitiesWithCompletion.filter(a => a.requiredForGraduation);
  const allRequiredComplete = requiredActivities.length > 0 &&
    requiredActivities.every(a => a.completed);

  const handleRequestGraduation = async () => {
    setIsRequestingGraduation(true);
    try {
      const result = await cohortsApi.requestGraduation(cohortId);
      setShowGraduationModal(false);
      if (result.graduated) {
        showSuccess('Congratulations! 🎓', result.message);
        // Invalidate permissions so the auth store reflects graduated status immediately
        await queryClient.invalidateQueries({
          queryKey: queryKeys.users.permissions(userId),
        });
        await queryClient.invalidateQueries({ queryKey: [...queryKeys.cohorts.detail(cohortId), 'participants'] });
        await queryClient.invalidateQueries({ queryKey: queryKeys.cohorts.userCompletions(cohortId, userId) });
      } else {
        setRequestSubmitted(true);
        showSuccess('Graduation requested', result.message);
      }
    } catch (err: any) {
      showError('Request failed', err.message || 'Could not submit graduation request. Please try again.');
    } finally {
      setIsRequestingGraduation(false);
    }
  };

  const handleOpenActivity = (activity: ActivityWithCompletion) => {
    // Block submission of mentor-approval activities when no mentor is assigned
    if (mentorsEnabled && activity.requiresMentorApproval && !hasMentor) {
      showError(
        'No mentor assigned',
        'A mentor must be assigned to you before you can submit this activity for review.'
      );
      return;
    }
    setSelectedActivity(activity);
    // Default completion date to today in YYYY/MM/DD format
    const today = new Date();
    const defaultDate = `${today.getFullYear()}/${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}`;
    setCompletionDate(activity.completedAt || defaultDate);
    setDeliverableName(activity.deliverableName || '');
    setDeliverableLink(activity.deliverableLink || '');
    setRecordingLink(activity.recordingLink || '');
  };

  const handleCloseModal = () => {
    setSelectedActivity(null);
    setCompletionDate('');
    setDeliverableName('');
    setDeliverableLink('');
    setRecordingLink('');
  };

  const handleSaveCompletion = async () => {
    if (!selectedActivity) return;
    setIsSubmitting(true);
    try {
      const result = await activitiesApi.markComplete(cohortId, selectedActivity.activityId, {
        completedAt: completionDate,
        deliverableName: deliverableName || undefined,
        deliverableLink: deliverableLink || undefined,
        recordingLink: recordingLink || undefined,
      });

      // Invalidate all queries that show completion state for this user:
      // 1. This tab's own completions list
      // 2. The overview tab's completions (drives the progress bar)
      // 3. The participants tab (completion_percentage column)
      await queryClient.invalidateQueries({
        queryKey: queryKeys.activities.completions(userId, cohortId),
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.cohorts.userCompletions(cohortId, userId),
      });
      // Invalidate all participants queries for this cohort regardless of statusFilter
      await queryClient.invalidateQueries({
        queryKey: [...queryKeys.cohorts.detail(cohortId), 'participants'],
      });

      if (result.requiresApproval) {
        showSuccess('Activity submitted', 'Your completion has been submitted for mentor approval.');
      } else {
        showSuccess('Activity completed', 'Your activity has been marked as complete!');
      }
      handleCloseModal();
    } catch {
      showError('Failed to save', 'Could not save activity completion. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (activitiesLoading) return <LoadingSpinner message="Loading activities..." />;
  if (activitiesError) return <ErrorAlert error={activitiesError} />;

  const { weeks, byWeek, unassigned } = groupByWeek(activitiesWithCompletion);
  const hasWeekTags = weeks.length > 0;

  // Column definitions — no Week column since it's the section header
  const columnDefs = [
    {
      id: 'title',
      header: 'Activity',
      cell: (item: ActivityWithCompletion) => (
        <SpaceBetween size="xxs">
          <Link
            variant="primary"
            onFollow={(e) => { e.preventDefault(); handleOpenActivity(item); }}
          >
            {item.title}
          </Link>
          {item.description && (
            <Box variant="small" color="text-body-secondary">
              <div style={{
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}>{item.description}</div>
            </Box>
          )}
          {item.linkUrl && (
            <Link external href={item.linkUrl} fontSize="body-s">{item.linkUrl}</Link>
          )}
        </SpaceBetween>
      ),
      isRowHeader: true,
      minWidth: 200,
    },
    {
      id: 'type',
      header: 'Type',
      cell: (item: ActivityWithCompletion) => item.activityType,
      width: 120,
    },
    {
      id: 'required',
      header: 'Required?',
      cell: (item: ActivityWithCompletion) => (
        item.requiredForGraduation ? <Badge color="blue">Required</Badge> : null
      ),
      width: 120,
    },
    ...(mentorsEnabled ? [{
      id: 'approval',
      header: 'Review?',
      cell: (item: ActivityWithCompletion) => {
        if (!item.requiresMentorApproval) return null;
        if (item.approvalStatus === 'completed' || item.approvalStatus === 'approved') return <Badge color="green">Approved</Badge>;
        if (item.pendingReview) return <Badge color="severity-medium">Pending</Badge>;
        return <Badge>Required</Badge>;
      },
      width: 150,
    }] : []),
    {
      id: 'status',
      header: 'Status',
      cell: (item: ActivityWithCompletion) => {
        if (item.completed) return (
          <SpaceBetween size="xxs">
            <StatusIndicator type="success">Complete</StatusIndicator>
            {item.completedAt && (
              <Box fontSize="body-s" color="text-body-secondary">
                {new Date(item.completedAt).toLocaleDateString()}
              </Box>
            )}
          </SpaceBetween>
        );
        if (item.pendingReview) return (
          <StatusIndicator type="in-progress">Pending review</StatusIndicator>
        );
        // Mentor-approval activity but no mentor assigned yet — show blocked state
        if (mentorsEnabled && item.requiresMentorApproval && !hasMentor) return (
          <SpaceBetween size="xxs">
            <StatusIndicator type="pending">Incomplete</StatusIndicator>
            <Box fontSize="body-s" color="text-status-warning">
              Requires mentor assignment
            </Box>
          </SpaceBetween>
        );
        return (
          <SpaceBetween size="xxs">
            <StatusIndicator type="pending">Incomplete</StatusIndicator>
            <Link
              fontSize="body-s"
              onFollow={(e) => { e.preventDefault(); handleOpenActivity(item); }}
            >
              Mark as complete
            </Link>
          </SpaceBetween>
        );
      },
      width: 150,
    },
  ];

  const renderTable = (items: ActivityWithCompletion[]) => (
    <Table
      trackBy="activityId"
      columnDefinitions={columnDefs}
      items={items}
      variant="borderless"
      wrapLines
      empty={
        <Box textAlign="center" color="inherit" padding={{ vertical: 'l' }}>
          <Box variant="strong">No activities</Box>
        </Box>
      }
    />
  );

  const renderWeekSection = (weekNum: number, items: ActivityWithCompletion[]) => {
    const dateRange = weekDateRange(weekNum, cohortStartDate);
    return (
      <Container
        key={weekNum}
        header={
          <Header
            variant="h3"
            description={dateRange || undefined}
          >
            Week {weekNum}
          </Header>
        }
      >
        {renderTable(items)}
      </Container>
    );
  };

  return (
    <SpaceBetween size="l">
      {/* Completion modal */}
      <Modal
        visible={!!selectedActivity}
        onDismiss={handleCloseModal}
        header={selectedActivity?.title}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleCloseModal}>Cancel</Button>
              <Button
                variant="primary"
                onClick={handleSaveCompletion}
                loading={isSubmitting}
                disabled={!completionDate}
              >
                Save
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        {selectedActivity && (
          <Form>
            <SpaceBetween size="l">
              <FormField label="Type">
                <Badge color="blue">{selectedActivity.activityType}</Badge>
              </FormField>

              <FormField label="Description">
                <Box>{selectedActivity.description || 'No description provided'}</Box>
              </FormField>

              {selectedActivity.linkUrl && (
                <FormField label="Activity Link">
                  <Link external href={selectedActivity.linkUrl}>
                    {selectedActivity.linkUrl}
                  </Link>
                </FormField>
              )}

              <FormField label="Mentor Approval">
                {selectedActivity.requiresMentorApproval
                  ? <Badge color="green">Required</Badge>
                  : <Badge color="grey">Not Required</Badge>
                }
              </FormField>

              <FormField
                label="Completion Date"
                description="When did you complete this activity?"
              >
                <DatePicker
                  value={completionDate}
                  onChange={({ detail }) => setCompletionDate(detail.value)}
                  placeholder="YYYY/MM/DD"
                />
              </FormField>

              <FormField
                label="Deliverable Name (Optional)"
                description="Name of your completed work, e.g. project title or certificate name."
              >
                <Input
                  value={deliverableName}
                  onChange={({ detail }) => setDeliverableName(detail.value)}
                  placeholder="e.g. GenAI SME Capstone — Cohort 1"
                />
              </FormField>

              <FormField
                label="Deliverable Link (Optional)"
                description="Link to your completed work, repository, document, etc."
              >
                <Input
                  value={deliverableLink}
                  onChange={({ detail }) => setDeliverableLink(detail.value)}
                  placeholder="https://..."
                />
              </FormField>

              <FormField
                label="Recording Link (Optional)"
                description="Link to a recording of your presentation or demo."
              >
                <Input
                  value={recordingLink}
                  onChange={({ detail }) => setRecordingLink(detail.value)}
                  placeholder="https://..."
                />
              </FormField>

              {selectedActivity.completed && (
                <FormField label="Status">
                  <StatusIndicator type="success">
                    Completed on {selectedActivity.completedAt && new Date(selectedActivity.completedAt).toLocaleDateString()}
                  </StatusIndicator>
                </FormField>
              )}
            </SpaceBetween>
          </Form>
        )}
      </Modal>

      {/* Graduation confirmation modal */}
      <Modal
        visible={showGraduationModal}
        onDismiss={() => setShowGraduationModal(false)}
        header="Request Graduation"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setShowGraduationModal(false)}>Cancel</Button>
              <Button
                variant="primary"
                loading={isRequestingGraduation}
                onClick={handleRequestGraduation}
              >
                {graduationApproval === 'automatic' ? 'Graduate' : 'Submit Request'}
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Box>
            {graduationApproval === 'automatic'
              ? 'You have completed all required activities. Confirming will mark you as graduated from this cohort.'
              : 'You have completed all required activities. Your graduation request will be sent to the program admins for approval.'
            }
          </Box>
        </SpaceBetween>
      </Modal>

      {/* Overall progress header */}
      <Header
        variant="h2"
        description="Complete all required activities to graduate from this cohort"
        actions={
          isAlreadyGraduated ? (
            <Badge color="green">🎓 Graduated</Badge>
          ) : (
            <Button
              variant="primary"
              disabled={!allRequiredComplete || requestSubmitted}
              disabledReason={
                requestSubmitted
                  ? 'Your graduation request is pending approval'
                  : requiredActivities.length === 0
                  ? 'No required activities defined for this cohort'
                  : 'Complete all required activities to request graduation'
              }
              onClick={() => setShowGraduationModal(true)}
            >
              {graduationApproval === 'automatic' ? 'Graduate' : 'Request Graduation'}
            </Button>
          )
        }
      >
        My Activities ({completedCount} of {totalCount} completed)
      </Header>

      {totalCount === 0 ? (
        <Box textAlign="center" color="text-body-secondary" padding={{ vertical: 'l' }}>
          <Box variant="strong">No activities yet</Box>
          <Box variant="p">Program admins will add required activities for this cohort.</Box>
        </Box>
      ) : (
        <SpaceBetween size="l">
          {/* Unassigned activities first */}
          {unassigned.length > 0 && (
            <Container
              header={
                <Header variant="h3">
                  {hasWeekTags ? 'Additional Activities' : 'Activities'}
                </Header>
              }
            >
              {renderTable(unassigned)}
            </Container>
          )}

          {/* Weekly activities after */}
          {weeks.map(weekNum => renderWeekSection(weekNum, byWeek[weekNum]))}
        </SpaceBetween>
      )}
    </SpaceBetween>
  );
}
